import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

type RefType = {
  current: HTMLElement | null;
};

/**
 * Animates the CTA heading with a fade-in and slide-up effect
 */
export const animateHeading = (
  headingRef: RefType,
  sectionRef: RefType,
  delay: number = 0
): ScrollTrigger | undefined => {
  if (!headingRef.current || !sectionRef.current) return;

  const headingTrigger = ScrollTrigger.create({
    id: "locations-cta-heading",
    trigger: sectionRef.current,
    start: "top 80%",
    once: true,
  });

  gsap.fromTo(
    headingRef.current,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.7,
      delay,
      ease: "power3.out",
      scrollTrigger: headingTrigger,
    }
  );

  return headingTrigger;
};

/**
 * Animates the CTA description text with a fade-in effect
 */
export const animateDescription = (
  descriptionRef: RefType,
  sectionRef: RefType,
  delay: number = 0.2
): ScrollTrigger | undefined => {
  if (!descriptionRef.current || !sectionRef.current) return;

  const descriptionTrigger = ScrollTrigger.create({
    id: "locations-cta-description",
    trigger: sectionRef.current,
    start: "top 80%",
    once: true,
  });

  gsap.fromTo(
    descriptionRef.current,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.7,
      delay,
      ease: "power3.out",
      scrollTrigger: descriptionTrigger,
    }
  );

  return descriptionTrigger;
};

/**
 * Animates the CTA buttons with a staggered fade-in and slide-up effect
 */
export const animateButtons = (
  buttonsRef: RefType,
  sectionRef: RefType,
  delay: number = 0.4
): ScrollTrigger | undefined => {
  if (!buttonsRef.current || !sectionRef.current) return;

  const buttons = buttonsRef.current.querySelectorAll("a");
  if (!buttons.length) return;

  const buttonsTrigger = ScrollTrigger.create({
    id: "locations-cta-buttons",
    trigger: sectionRef.current,
    start: "top 80%",
    once: true,
  });

  gsap.fromTo(
    buttons,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.6,
      stagger: 0.15,
      delay,
      ease: "power2.out",
      scrollTrigger: buttonsTrigger,
    }
  );

  return buttonsTrigger;
};

/**
 * Cleans up ScrollTrigger instances specific to this component
 */
export const cleanupScrollTriggers = (): void => {
  ScrollTrigger.getAll()
    .filter((trigger) => {
      const id = trigger.vars.id;
      return id && id.toString().startsWith("locations-cta-");
    })
    .forEach((trigger) => trigger.kill());
};
